import * as R from 'ramda'
import * as React from 'react'
import {connect} from 'react-redux'
import type {
  TGetGameDataResponse,
  TGetGameTimeResponse,
} from '@kapitalist/contract/endpoints'
import {defaultRuleset} from '@kapitalist/contract/ruleset'
import type {
  TGameData,
  TGameId,
  TGameSpectatorData,
} from '@kapitalist/contract/types'
import {values} from '@kapitalist/utils/Map'
import {createSafe} from '@kapitalist/utils/NEList'
import {safely} from '@kapitalist/utils/safely'
import * as S from '@kapitalist/utils/Set'
import api from '~/api/endpoints'
import GameLobbyV2Layout from '~/layouts/GameLobbyV2Layout'
import GameData from '~/models/game/GameData'
import {toCreateMajorActionData} from '~/models/game/toCreateMajorActionData'
import {toCreateMinorActionData} from '~/models/game/toCreateMinorActionData'
import GameLobbyV2UI from '~/pages/GameLobbyV2/stateless'
import NotFound from '~/pages/NotFound'
import {addPastGamePositionsForGame} from '~/redux/reducers/pastGamePositions'
import type {ReduxState} from '~/redux/reducers/root'
import {setSpectatorData, spectatorsDataForPlayer} from '~/redux/reducers/spectator'
import type {ISignedInRouteComponentProps} from '~/routing/Route/SignedIn'
import {setStatePromise} from '~/utils/React'
import type {TReactSetStateStateParameter} from '~/utils/types'
import type {TAttackDirection} from '~/utils/UserNotifications'
import {sendNotification} from '~/utils/UserNotifications'
import type {IGameLobbyPagePathPieces} from './types'

interface IReduxProps extends Pick<
  ReduxState,
  'settingsState' | 'spectatorState' | 'pastGamePositionsState'
> {}
interface IProps
  extends
    ISignedInRouteComponentProps<IGameLobbyPagePathPieces>,
    IReduxProps,
    TDispatch {}

interface IState {
  currentGameData: GameData | undefined
  soundsEnabled: boolean
  gameDataRequestsPending: number
}

class GameLobbyV2Page extends React.Component<IProps, IState> {
  state: IState = {
    currentGameData: undefined,
    soundsEnabled: false,
    gameDataRequestsPending: 0,
  }

  receiveNewGameDataResponse =
    (initialLoad: boolean) => (gameDataResponse: TGetGameDataResponse) => {
      // NB: anything done here should try to be efficient and only set state when
      // necessary, since otherwise it causes the whole lobby to rerender, which
      // could cause performance impacts later
      this.receiveNewGameData(gameDataResponse.gameData)
      this.receiveNewSpectatorData(initialLoad)(gameDataResponse.spectatorRequests)
    }

  receiveNewGameData = (gameData: TGameData) => {
    const oldGameData = this.state.currentGameData
    const newMD5 = GameData.hash(gameData)

    if (newMD5 === oldGameData?.hash) {
      // avoid re-rendering the whole lobby if we don't need to on this poll
      return
    }

    const gameDataModel = new GameData(
      gameData,
      this.props.authenticatedPlayer.id,
      this.props.settingsState.betaFeatures.gameLog,
      newMD5
    )

    // store pastGamePositions in redux since the backend doesn't send the old ones?
    this.props.addPastGamePositionsForGame({
      gameId: gameDataModel.gameId,
      currentTime: gameData.currentGamePosition.time,
      pastGamePositions: gameDataModel.pastGamePositions ?? [],
    })

    this.setState({currentGameData: gameDataModel}, () => {
      // NB: this callback is all just for user notifications
      const newGameData = this.state.currentGameData
      if (!oldGameData || !newGameData) {
        return
      }
      const newGamePos = newGameData.playerGamePosition
      const oldGamePos = oldGameData.playerGamePosition

      if (oldGamePos?.isMyMajorTurn() !== newGamePos?.isMyMajorTurn()) {
        sendNotification(
          {
            type: 'turnChange',
            isMyTurn: newGamePos?.isMyMajorTurn() ?? false,
          },
          this.props.settingsState.notifications
        )
      } else if (
        oldGamePos?.inProgressMajorAction === undefined &&
        newGamePos?.inProgressMajorAction
      ) {
        // new attack
        const attackDirection: TAttackDirection =
          newGamePos.inProgressMajorAction.majorActor.id ===
          this.props.authenticatedPlayer.id
            ? 'fromMe'
            : newGamePos.inProgressMajorAction.pendingMinorActions.find(
                  pma => pma.minorActorPlayerId === this.props.authenticatedPlayer.id
                )
              ? 'againstMe'
              : 'uninvolved'
        sendNotification(
          {
            type: 'attack',
            direction: attackDirection,
          },
          this.props.settingsState.notifications
        )
      }
    })
  }

  receiveNewSpectatorData =
    (initialLoad: boolean) => (spectatorData: TGameSpectatorData[]) => {
      if (R.equals(this.props.spectatorState.spectatorData, spectatorData)) {
        // avoid re-rendering the whole lobby if we don't need to on this poll
        return
      }

      // update Redux
      this.props.setSpectatorData(spectatorData)

      if (initialLoad) {
        // no notification on first page load
        return
      }

      // previously denied requests are treated the same as nonexistent requests
      const oldSpectatorData = spectatorsDataForPlayer(
        this.props.authenticatedPlayer.id,
        this.props.spectatorState.spectatorData
      ).filter(sd => sd.status !== 'denied')
      const newSpectatorData = spectatorsDataForPlayer(
        this.props.authenticatedPlayer.id,
        spectatorData
      ).filter(sd => sd.status !== 'denied')

      const oldSpectatorsIds = new Set(
        spectatorsDataForPlayer(
          this.props.authenticatedPlayer.id,
          oldSpectatorData
        ).map(sd => sd.spectatorPlayer.playerId)
      )
      const newSpectatorsIds = new Set(
        spectatorsDataForPlayer(
          this.props.authenticatedPlayer.id,
          newSpectatorData
        ).map(sd => sd.spectatorPlayer.playerId)
      )

      if (!S.subset(newSpectatorsIds, oldSpectatorsIds)) {
        // new spectator
        sendNotification({type: 'spectator'}, this.props.settingsState.notifications)
      }
    }

  // we poll this to see if we need to fetch new game data or not
  receiveGameTime = (r: TGetGameTimeResponse) => {
    const {currentGameData} = this.state
    // not just `<=` to support rollbacks in prod while debugging
    if (!currentGameData || r.time === currentGameData.currentGamePosition.time) {
      return
    }
    this.fetchLatestGameData(currentGameData.gameId)
  }

  fetchLatestGameData = (gameId: TGameId) => {
    // pause the time poller while we fetch the updated game data
    void setStatePromise(this, GameLobbyV2Page.requestsPendingStateChange(1))
      .then(() => api.preGame.getGameData.execute({}, {maybeGameId: gameId}))
      .then(this.receiveNewGameDataResponse(false))
      .catch((e: unknown) => console.warn('Failed to fetch updated game data', e))
      .finally(() => this.setState(GameLobbyV2Page.requestsPendingStateChange(-1)))
  }

  static requestsPendingStateChange =
    (delta: -1 | 1): TReactSetStateStateParameter<IState, any, any> =>
    prevState => ({
      gameDataRequestsPending: Math.max(
        0,
        prevState.gameDataRequestsPending + delta
      ),
    })

  render() {
    const rules = defaultRuleset
    const {settingsState} = this.props
    const maybeGameId = this.props.match.params.maybeGameId

    // NB: for now, we are polling a cheap getGameTime rather than the full getGameData.
    // this allows polling to happen in a tighter loop.
    // but this means that things that change in getGameData _without_ a time change
    // won't be noticed until the next time change (e.g. spectator requests).
    // accepting this downside for now to get the faster polling benefit.
    return (
      <GameLobbyV2Layout {...this.props}>
        <api.preGame.getGameData.PromiseManager
          params={{}}
          pathPieces={{maybeGameId}}
          onSuccess={this.receiveNewGameDataResponse(true)}
          then={() => {
            const {currentGameData} = this.state
            if (!currentGameData) {
              return <NotFound />
            }
            const {gameId} = currentGameData

            return (
              <>
                <api.preGame.getGameTime.PromisePoller
                  params={{}}
                  pathPieces={{maybeGameId: gameId}}
                  onSuccess={this.receiveGameTime}
                  frequencyMs={[1000, 1000, 3000, 5000, -1]}
                  pollImmediatelyOnMount={false}
                  pause={this.state.gameDataRequestsPending > 0}
                  restartKey={this.state.currentGameData?.currentGamePosition.time}
                />
                <GameLobbyV2UI
                  gameData={currentGameData}
                  settingsState={settingsState}
                  rules={rules}
                  chooseMajorAction={majorAction =>
                    setStatePromise(
                      this,
                      GameLobbyV2Page.requestsPendingStateChange(1)
                    ).then(() =>
                      api.inGame.submitMajorAction
                        .execute(
                          {
                            time: currentGameData.currentGamePosition.time,
                            action: toCreateMajorActionData(majorAction),
                          },
                          {gameId}
                        )
                        .finally(() =>
                          this.setState(
                            GameLobbyV2Page.requestsPendingStateChange(-1)
                          )
                        )
                    )
                  }
                  chooseMajorActionSubmitSuccess={r =>
                    this.receiveNewGameData(r.gameData)
                  }
                  chooseMinorAction={minorAction =>
                    setStatePromise(
                      this,
                      GameLobbyV2Page.requestsPendingStateChange(1)
                    ).then(() =>
                      api.inGame.submitMinorAction
                        .execute(
                          {
                            time: currentGameData.currentGamePosition.time,
                            action: toCreateMinorActionData(minorAction),
                          },
                          {gameId}
                        )
                        .finally(() =>
                          this.setState(
                            GameLobbyV2Page.requestsPendingStateChange(-1)
                          )
                        )
                    )
                  }
                  chooseMinorActionSubmitSuccess={r =>
                    this.receiveNewGameData(r.gameData)
                  }
                  allPastGamePositions={createSafe(
                    R.sortWith(
                      [R.ascend(a => a.date), R.ascend(a => a.gamePosition.time)],
                      values(
                        safely(this.state.currentGameData?.gameId, gid =>
                          this.props.pastGamePositionsState.games.get(gid)
                        )?.pastGamePositionsMap ?? new Map()
                      )
                    )
                  )}
                />
              </>
            )
          }}
        />
      </GameLobbyV2Layout>
    )
  }
}

const mapStateToProps = (state: ReduxState): IReduxProps => ({
  settingsState: state.settingsState,
  spectatorState: state.spectatorState,
  pastGamePositionsState: state.pastGamePositionsState,
})

const mapDispatchToProps = {setSpectatorData, addPastGamePositionsForGame}
type TDispatch = typeof mapDispatchToProps

export default connect(mapStateToProps, mapDispatchToProps)(GameLobbyV2Page)
